/**
 * Contract detail (`/contrats/:idNpol?id_per=…` — fiches 22803 / 22805 /
 * 22702 / 22717 / 22719 / 22723 / 22721).
 *
 * - Data: the seven VEOS calls fired eagerly in parallel
 *   (`useContractDetail`); the « Assuré » call only when `id_per` is in the
 *   URL (`WS2_EA_FIC_POL` does not return the insured id) — without it the
 *   tab shows « aucune donnée disponible ».
 * - Header: title = insured name carried by the list row (router state)
 *   else `NUM_POL`; subtitle `LIB_PROD · NUM_POL`; breadcrumb
 *   Contrats › NUM_POL; back to the list.
 * - `Tabs` with stable values `details`, `assure`, `primes` (n), `garanties`,
 *   `sinistres` (n), `documents`, `pieces` (n « EN ATTENTE »); every panel is
 *   a registered configurable card.
 * - Rule context: the extranet base + `contract_id`, `contract_status`,
 *   `contract_product`, `contract_product_label`, `insurer`.
 */

import { useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { Badge } from '@components/base/display';
import { Tabs } from '@components/base/navigation';
import { RuleContextProvider } from '@components/shared/configurable';

import { countPendingPieces } from '../api/contractsService';
import { useExtranetAuthStore } from '../auth/store';
import { PageHeader } from '../components/PageHeader';
import { buildContractDetailRuleContext } from '../components/contracts/contractContext';
import {
  CONTRACT_DETAIL_DEFAULT_TAB,
  CONTRACT_DETAIL_TABS,
  type ContractDetailTabCount,
} from '../components/contracts/contractDetailTabs';
import {
  contractDetailsValues,
  contractInsuredValues,
} from '../components/contracts/contractInfoFields';
import {
  CONTRACT_ID_PER_PARAM,
  readContractRouteState,
} from '../components/contracts/contractNavigation';
import {
  ContractClaimsCardConfigurable as ContractClaimsCard,
  ContractDetailsCardConfigurable as ContractDetailsCard,
  ContractDocumentsCardConfigurable as ContractDocumentsCard,
  ContractGuaranteesCardConfigurable as ContractGuaranteesCard,
  ContractInsuredCardConfigurable as ContractInsuredCard,
  ContractPiecesCardConfigurable as ContractPiecesCard,
  ContractPremiumsCardConfigurable as ContractPremiumsCard,
} from '../components/contracts/register';
import { useContractDetail } from '../components/contracts/useContractDetail';
import { useExtranetBaseRuleContext } from '../contextVariables';
import { EXTRANET_PAGE_TEXT, extranetPath } from '../navigation';

/** Stable empty list while a section is loading / failed. */
const NO_ROWS: readonly never[] = [];

/** Join the non-empty parts with « · ». */
function joinDot(...parts: Array<string | null | undefined>): string {
  return parts.filter((part): part is string => Boolean(part)).join(' · ');
}

export function ContractDetailPage() {
  const { tenantSlug, idNpol = '' } = useParams<{ tenantSlug: string; idNpol: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);

  const idPerParam = searchParams.get(CONTRACT_ID_PER_PARAM)?.trim() ?? '';
  const idPer = idPerParam === '' ? null : idPerParam;
  const routeState = useMemo(() => readContractRouteState(location.state), [location.state]);

  const data = useContractDetail(registrySlug, idNpol, idPer);
  const detail = data.detail.data ?? null;

  // --- header ------------------------------------------------------------
  const text = EXTRANET_PAGE_TEXT.contract;
  const listPath = extranetPath(tenantSlug, 'contrats');
  const numero = detail?.numero ?? routeState.numero ?? null;
  const produit = detail?.produit ?? routeState.produit ?? null;
  const title = routeState.assure ?? numero ?? `${text.title} ${idNpol}`;
  const subtitle = joinDot(produit, numero);

  // --- rule context ------------------------------------------------------
  const baseContext = useExtranetBaseRuleContext();
  const contractContext = buildContractDetailRuleContext(idNpol, detail);
  const { contract_id, contract_status, contract_product, contract_product_label, insurer } =
    contractContext;
  const ruleContext = useMemo(
    () => ({
      ...baseContext,
      contract_id,
      contract_status,
      contract_product,
      contract_product_label,
      insurer,
    }),
    [baseContext, contract_id, contract_status, contract_product, contract_product_label, insurer],
  );

  // --- card data -----------------------------------------------------------
  const detailsValues = useMemo(() => contractDetailsValues(data.detail.data), [data.detail.data]);
  const insuredValues = useMemo(
    () => contractInsuredValues(data.insured.data),
    [data.insured.data],
  );
  const claimRows = useMemo(
    () =>
      (data.claims.data ?? []).map((row) => ({
        ...row,
        to: extranetPath(tenantSlug, 'sinistres', row.id),
      })),
    [data.claims.data, tenantSlug],
  );

  const counts: Record<ContractDetailTabCount, number | undefined> = {
    premiums: data.premiums.data?.length,
    claims: data.claims.data?.length,
    pendingPieces: data.pieces.data ? countPendingPieces(data.pieces.data) : undefined,
  };

  const [tab, setTab] = useState<string>(CONTRACT_DETAIL_DEFAULT_TAB);

  return (
    <RuleContextProvider value={ruleContext}>
      <div data-testid="extranet-page-contract">
        <PageHeader
          title={title}
          subtitle={subtitle === '' ? undefined : subtitle}
          backTo={listPath}
          breadcrumbs={[
            { key: 'contrats', label: text.breadcrumb, to: listPath },
            { key: 'contrat', label: numero ?? idNpol },
          ]}
        />
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Tabs value={tab} onChange={setTab}>
            <Tabs.List className="overflow-x-auto" aria-label="Sections du contrat">
              {CONTRACT_DETAIL_TABS.map((entry) => {
                const count = entry.count ? counts[entry.count] : undefined;
                return (
                  <Tabs.Tab key={entry.key} value={entry.key} data-tab-key={entry.key}>
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      {entry.label}
                      {count !== undefined && count > 0 && (
                        <Badge
                          size="sm"
                          pill
                          variant={entry.countVariant ?? 'default'}
                          data-testid={`contract-tab-count-${entry.key}`}
                        >
                          {count}
                        </Badge>
                      )}
                    </span>
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>

            <Tabs.Panel value="details">
              <ContractDetailsCard
                values={detailsValues}
                loading={data.detail.loading}
                error={data.detail.error}
              />
            </Tabs.Panel>
            <Tabs.Panel value="assure">
              <ContractInsuredCard
                values={data.insured.enabled ? insuredValues : null}
                loading={data.insured.enabled && data.insured.loading}
                error={data.insured.enabled ? data.insured.error : null}
              />
            </Tabs.Panel>
            <Tabs.Panel value="primes">
              <ContractPremiumsCard
                rows={data.premiums.data ?? NO_ROWS}
                loading={data.premiums.loading}
                error={data.premiums.error}
              />
            </Tabs.Panel>
            <Tabs.Panel value="garanties">
              <ContractGuaranteesCard
                rows={data.guarantees.data ?? NO_ROWS}
                loading={data.guarantees.loading}
                error={data.guarantees.error}
              />
            </Tabs.Panel>
            <Tabs.Panel value="sinistres">
              <ContractClaimsCard
                rows={claimRows}
                loading={data.claims.loading}
                error={data.claims.error}
              />
            </Tabs.Panel>
            <Tabs.Panel value="documents">
              <ContractDocumentsCard
                documents={data.documents.data ?? NO_ROWS}
                loading={data.documents.loading}
                error={data.documents.error}
              />
            </Tabs.Panel>
            <Tabs.Panel value="pieces">
              <ContractPiecesCard
                pieces={data.pieces.data ?? NO_ROWS}
                loading={data.pieces.loading}
                error={data.pieces.error}
              />
            </Tabs.Panel>
          </Tabs>
        </div>
      </div>
    </RuleContextProvider>
  );
}
